/**
 * PRP-WEB-2 W2.6: the Problems strip above the plan. Overlaps in red, estimated sizes in amber,
 * rooms with no door in grey; a click selects the room. Fix buttons: "Nudge apart" (an overlap
 * under 2 ft) and "Move inside <room>" (a closet subroom away from its main room).
 */
import { useState } from 'react';
import { AlertTriangle, ChevronDown, ChevronUp } from 'lucide-react';
import { findRoom, type Plan } from '@sketch-core';
import { NUDGE_MAX_FT, type Problem } from './problems';

const TONE: Record<Problem['kind'], string> = {
  overlap: 'border-[var(--bad)] text-[var(--bad)]',
  'closet-outside': 'border-[var(--bad)] text-[var(--bad)]',
  estimated: 'border-[var(--warn)] text-[var(--warn)]',
  'no-door': 'border-[var(--bd)] text-[var(--tx-mid)]',
  'touches-nothing': 'border-[var(--warn)] text-[var(--warn)]',
};

export interface ProblemsStripProps {
  plan: Plan;
  problems: Problem[];
  onSelect: (rid: string) => void;
  onNudge: (rid: string, other: string) => void;
  onMoveInside: (rid: string, host: string) => void;
}

export function ProblemsStrip({ plan, problems, onSelect, onNudge, onMoveInside }: ProblemsStripProps) {
  const [open, setOpen] = useState(true);
  if (!problems.length) return null;
  const shown = open ? problems : [];
  return (
    <div data-testid="plan-problems" aria-label="Problems on this plan"
      className="flex flex-wrap items-center gap-1.5 border-b border-[var(--bd)] bg-[var(--bg-1)] px-3 py-1.5 sm:px-4">
      <button type="button" onClick={() => setOpen(!open)} aria-expanded={open}
        className="flex items-center gap-1 rounded-[var(--r-sm)] px-1 text-[12px] font-[620] text-[var(--tx-hi)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent-line)]">
        <AlertTriangle className="h-3.5 w-3.5 text-[var(--warn)]" aria-hidden />
        {problems.length} to check
        {open ? <ChevronUp className="h-3.5 w-3.5" aria-hidden /> : <ChevronDown className="h-3.5 w-3.5" aria-hidden />}
      </button>
      {shown.map((p, i) => (
        <span key={`${p.kind}-${p.rid}-${i}`} data-testid={`problem-${p.kind}`}
          className={`flex items-center gap-1 rounded-full border bg-[var(--bg-2)] py-0.5 pl-2 pr-1 text-[11.5px] ${TONE[p.kind]}`}>
          <button type="button" onClick={() => onSelect(p.rid)} title="Show this room"
            className="rounded-[var(--r-sm)] hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent-line)]">{p.text}</button>
          {p.kind === 'overlap' && p.depthFt < NUDGE_MAX_FT && (
            <FixButton testId="problem-nudge" onClick={() => onNudge(p.rid, p.other)}>Nudge apart</FixButton>
          )}
          {p.kind === 'closet-outside' && (
            <FixButton testId="problem-move-inside" onClick={() => onMoveInside(p.rid, p.host)}>Move inside {findRoom(plan, p.host)?.name ?? 'its room'}</FixButton>
          )}
        </span>
      ))}
    </div>
  );
}

function FixButton({ onClick, children, testId }: { onClick: () => void; children: React.ReactNode; testId: string }) {
  return (
    <button type="button" data-testid={testId} onClick={onClick}
      className="rounded-full bg-[var(--bg-4)] px-2 py-0.5 text-[11px] font-[600] text-[var(--tx-hi)] hover:bg-[var(--accent)] hover:text-[var(--accent-tx)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent-line)]">
      {children}
    </button>
  );
}

export default ProblemsStrip;
